// The placed girders: `XGirderGeomSet` in `FWDP_Transparent:WS_LandscapeMID`
// (`WS_DestructionManager::Girder::__CreateGirderManagerGeometry` 0xe00f10), filled from
// `m_GIRDERS_List` by `WS_DestructionManager::Update` 0xe06190 (the sim's `W` landscape record,
// buildings.ts) and drawn by `XomOglDrawGirderGeomSet` 0x11a4d50 with Root.System.WSGirder{V,F}:
// per girder a quad of half extents `dimensions` rotated by `angle × π / 180` around
// `centerPosition`, the type's texture (`g_pGirderTexNames`: WE_Girder_01, WE_Girder_02,
// WE_GirderGirth_01, WE_GirderGirth_02 of the In-Game bundle; clamp, linear, no mips) at
// `uvMultiplier = dimensions / texture size`, `AdditiveColor = (r, status == 1 ? 0 : 1, b, a)`;
// blend SRC_ALPHA, ONE_MINUS_SRC_ALPHA, no depth, no culling. The shader reads the damage target:
// a placed girder (status 2) vanishes where a later blast cut the land (R >= 1 without the
// girder mark), shows soil (`DamagedLand` at 256 units) where 0.5 < R, and takes the rim.
import * as THREE from 'three';
import type { GirderView } from './buildings.ts';
import { xomTexture } from './land.ts';

const VERT = /* glsl */ `
uniform vec3 centerPosition;
uniform float angle;
uniform vec2 dimensions;
out vec2 vUv;
out vec2 vWorld;
void main() {
  vUv = (position.xy + 1.0) * 0.5;
  float s = sin(angle), c = cos(angle);
  vec2 rp = vec2(position.x * dimensions.x * c - position.y * dimensions.y * s, position.x * dimensions.x * s + position.y * dimensions.y * c);
  vWorld = rp + centerPosition.xy;
  gl_Position = projectionMatrix * viewMatrix * vec4(vWorld, centerPosition.z, 1.0);
}`;

const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D sTexture;
uniform sampler2D sDamage;
uniform sampler2D sDamagedLand;
uniform vec4 AdditiveColor;
uniform float waterLevel;
uniform vec2 uvMultiplier;
in vec2 vUv;
in vec2 vWorld;
void main() {
  if (vWorld.y < waterLevel) discard;
  vec2 damageUV = (vWorld + vec2(4096.0, 2048.0)) / vec2(8192.0, 4096.0);
  vec4 damageValue = texture(sDamage, damageUV);
  bool isGirder = damageValue.a == 1.0;
  vec2 colorUV = vec2(vUv.x * uvMultiplier.x, (1.0 - uvMultiplier.y) + vUv.y * uvMultiplier.y);
  vec4 landColour = texture(sTexture, colorUV);
  landColour.rb += AdditiveColor.rb;
  damageValue.r *= AdditiveColor.g;
  if (damageValue.r >= 1.0 && !isGirder) discard;
  else if (damageValue.r > 0.5 && !isGirder) {
    landColour.rgb = texture(sDamagedLand, damageUV * vec2(32.0, 16.0)).rgb;
  } else {
    vec3 blend = vec3(178.0, 170.0, 147.0) / 255.0;
    vec3 base = vec3(damageValue.g);
    vec3 overlay = mix(2.0 * base * blend, 1.0 - 2.0 * (1.0 - base) * (1.0 - blend), step(0.5, base));
    landColour.rgb = mix(landColour.rgb, overlay, damageValue.b * AdditiveColor.g);
  }
  gl_FragColor = landColour;
}`;

const TEXTURES = ['WE_Girder_01', 'WE_Girder_02', 'WE_GirderGirth_01', 'WE_GirderGirth_02'];
/** `π / 180` as `XomOglDrawGirderGeomSet` computes it (f32 .rodata 0x158f90c, 0x158f908). */
const PI_F = Math.fround(Math.PI), DEG_F = 180;

export class GirderLayer {
  readonly group = new THREE.Group();
  private textures: (THREE.Texture | null)[] = [null, null, null, null];
  private sizes: [number, number][] = [[1, 1], [1, 1], [1, 1], [1, 1]];
  private meshes: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>[] = [];
  private geometry = new THREE.BufferGeometry();

  private damage: { value: THREE.Texture };
  private soil: { value: THREE.Texture | null };
  private water: { value: number };
  private order: number;

  constructor(damage: { value: THREE.Texture }, soil: { value: THREE.Texture | null }, water: { value: number }, order: number) {
    this.damage = damage;
    this.soil = soil;
    this.water = water;
    this.order = order;
    this.geometry.setAttribute('position', new THREE.Float32BufferAttribute([1, -1, 0, -1, -1, 0, -1, 1, 0, 1, 1, 0, 1, -1, 0, -1, 1, 0], 3));
  }

  /** The four girder textures (In-Game bundle). */
  async load(): Promise<void> {
    await Promise.all(TEXTURES.map(async (name, i) => {
      const t = await xomTexture(`xom/DataLinux/Bundles/In-Game/${name}.json`, THREE.ClampToEdgeWrapping);
      this.textures[i] = t.texture;
      this.sizes[i] = [t.width, t.height];
    }));
  }

  /** The girder geomset's list as the last `WS_DestructionManager::Update` left it. */
  set(girders: GirderView[]): void {
    while (this.meshes.length < girders.length) {
      const mat = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          centerPosition: { value: new THREE.Vector3() }, angle: { value: 0 }, dimensions: { value: new THREE.Vector2() },
          sTexture: { value: null }, sDamage: this.damage, sDamagedLand: this.soil, AdditiveColor: { value: new THREE.Vector4() },
          waterLevel: this.water, uvMultiplier: { value: new THREE.Vector2() },
        },
        transparent: true,
        blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation, blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
        blendEquationAlpha: THREE.AddEquation, blendSrcAlpha: THREE.SrcAlphaFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
        depthTest: false, depthWrite: false, side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(this.geometry, mat);
      mesh.frustumCulled = false;
      this.meshes.push(mesh);
      this.group.add(mesh);
    }
    this.meshes.forEach((mesh, i) => {
      const g = girders[i];
      mesh.visible = !!g && !!this.textures[g.type];
      if (!mesh.visible) return;
      mesh.renderOrder = this.order + i / 1000;
      const u = mesh.material.uniforms;
      u.centerPosition.value.set(g.pos[0], g.pos[1], g.pos[2]);
      u.angle.value = Math.fround(Math.fround(PI_F * g.angle) / DEG_F);
      u.dimensions.value.set(g.dims[0], g.dims[1]);
      u.AdditiveColor.value.set(g.add[0], g.status === 1 ? 0 : 1, g.add[2], g.add[3]);
      u.sTexture.value = this.textures[g.type];
      const [w, h] = this.sizes[g.type];
      u.uvMultiplier.value.set(Math.fround(g.dims[0] / w), Math.fround(g.dims[1] / h));
    });
  }
}
